import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import range from 'lodash/range';
import round from 'lodash/round';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';

import types from '../../../../../types';
import BlankLink from '../../../../common/BlankLink';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';

const percentageOptions = t => range(0.20, 2.20, 0.2).concat([2.5, 3]).map(value => ({
  value,
  display: t('{{amount}}%', { amount: round(value * 100) }),
}));

const TurnUpSummerThermostat = ({
  t,
  isMetric,
  widgetSource,
  resultTakeactionThermostatSummerDollarstypical,
  resultTakeactionThermostatSummerKwhtypical,
  inputFootprintHousingSquarefeet,
  resultTakeactionThermostatSummerDollarsuse,
  resultTakeactionThermostatSummerKwhuse,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        <span
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line no-template-curly-in-string
            __html: t('Typical {{areaValue}} {{areaUnit}}<sup>2</sup> homes in my climate spend ${{dollarsTypical}} ({{kwhTypical}} kWh per year on air conditioning. I use the AC about', {
              areaValue: inputFootprintHousingSquarefeet.ensuredDisplay,
              areaUnit: isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' }),
              dollarsTypical: resultTakeactionThermostatSummerDollarstypical,
              kwhTypical: resultTakeactionThermostatSummerKwhtypical,
            }),
          }}
        />
        <InlineSelect
          options={percentageOptions(t)}
          apiKey={'input_takeaction_thermostat_summer_percentuse'}
        />
        {
          <span
            dangerouslySetInnerHTML={{
              // eslint-disable-next-line no-template-curly-in-string
              __html: t('of typical households, or ${{resultTakeactionThermostatSummerDollarsuse}} ({{resultTakeactionThermostatSummerKwhuse}} kWh per year. I will turn my thermostat up.', {
                resultTakeactionThermostatSummerDollarsuse,
                resultTakeactionThermostatSummerKwhuse,
              }),
            }}
          />
        }
        <InlineInput
          maxLength={2}
          apiKey={'input_takeaction_thermostat_summer_daydegrees'}
        />
        {t('degrees {{tempUnit}} on summer days and', { tempUnit: isMetric ? t('C', { ns: 'units' }) : t('F', { ns: 'units' }) })}
        <InlineInput
          maxLength={2}
          apiKey={'input_takeaction_thermostat_summer_nightdegrees'}
        />
        {t('degrees {{tempUnit}} on summer nights.', { tempUnit: isMetric ? t('C', { ns: 'units' }) : t('F', { ns: 'units' }) })}
      </p>
    </div>
    <div className="action-details__facts">
      {t('If you don\'t already do this, setting your thermostat to a neutral temperature while you\'re gone at work can immediately save you 10% in energy costs. Don\'t let your energy dollars seep away while you\'re busy earning \'em!')}
    </div>
    <div className="action-details__links">
      {widgetSource === 'ontario' &&
        <p>
          {t('You can get $100 towards a smart thermostat, which adapts to your ideal energy patterns, boosting your overall energy-awareness while offering comfort and savings you can rely on. To learn more about this program, check out')}
          &nbsp;
          <BlankLink href={`https://${t('greenon.ca/')}`} text="GreenON.ca" />.
        </p>
      }
      {widgetSource === 'ontario' &&
        <p>
          {t('Receive a no-cost smart thermostat. By signing up for the GreenON Installations program, a program representative will schedule an appointment, and install a smart thermostat and show you how to use it. They will also provide you with a customized low carbon, energy-saving solutions for your home. To learn more about this program, check out the')}
          &nbsp;
          <BlankLink href={`https://${t('greenon.ca/')}how-greenon-installations-works`} text={t('GreenON Installations Program')} />
          {t(' by the Green Ontario Fund.')}
        </p>
      }
      {widgetSource !== 'ontario' && <a
        href="http://www.coolcalifornia.org/funding-wizard#search//%255BZipcode%253D%253D94720%255D%255BEligible+Applicants%253D%253DIndividual%255D%255BCategory%253D%253DEnergy%255D/false/none/false/1/12"
        target="_blank"
        rel="noopener noreferrer"
      >{t('Find rebates and incentives for this action')}</a>}
    </div>
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('cost of electricity')}
          <InlineInput
            apiKey="input_takeaction_assumption_kwhprice"
            maxLength={3}
          />
          {t('cents per kWh')}
        </li>
        <li>
          {t('Cooling degree-days:')}
          <InlineInput
            apiKey="input_takeaction_thermostat_summer_cdd"
            maxLength={5}
          />
        </li>
        <li>
          <span
            dangerouslySetInnerHTML={{
              __html: t('Number of hours with temperature set for:'),
            }}
          />
          <ul>
            <li>
              {t('Weekdays during the day')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_summer_wdsetup"
              />
            </li>
            <li>
              {t('Weekdays at night')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_summer_wdntsetup"
              />
            </li>
            <li>
              {t('Weekends during the day')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_summer_wesetup"
              />
            </li>
            <li>
              {t('Weekends at night')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_summer_wentsetup"
              />
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
);

TurnUpSummerThermostat.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
  inputFootprintHousingSquarefeet: types.UserInput.isRequired,
  resultTakeactionThermostatSummerDollarstypical: PropTypes.string.isRequired,
  resultTakeactionThermostatSummerKwhtypical: PropTypes.string.isRequired,
  resultTakeactionThermostatSummerDollarsuse: PropTypes.string.isRequired,
  resultTakeactionThermostatSummerKwhuse: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionThermostatSummerDollarstypical',
  'resultTakeactionThermostatSummerKwhtypical',
  'resultTakeactionThermostatSummerDollarsuse',
  'resultTakeactionThermostatSummerKwhuse',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
  inputFootprintHousingSquarefeet: s.inputFootprintHousingSquarefeet(state),
});

const mapDispatchToProps = () => ({});

export default wrap(
  withT('thermostatSummer'),
  connect(mapStateToProps, mapDispatchToProps)(TurnUpSummerThermostat),
);
